import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowClockwise, DownloadSimple, Tray } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtDateTime } from "@/components/communication/commMeta";

const STATUS = {
  received: ["Empfangen", "bg-slate-100 text-slate-700"],
  processing: ["In Verarbeitung", "bg-sky-100 text-sky-800"],
  processed: ["Im Postfach", "bg-emerald-100 text-emerald-800"],
  failed: ["Fehlgeschlagen", "bg-rose-100 text-rose-800"],
  unassigned: ["Nicht zugeordnet", "bg-amber-100 text-amber-800"],
  rejected: ["Abgelehnt", "bg-orange-100 text-orange-800"],
  no_attachments: ["Kein Scan-Anhang", "bg-slate-200 text-slate-700"],
};

const errMsg = (e) => e?.response?.data?.detail || "Fehler";

function JobActions({ job, platform, tenants, onDone }) {
  const [tid, setTid] = useState("");
  const run = async (fn, okText) => {
    try {
      const { data } = await fn();
      if (data.status === "processed") toast.success(okText);
      else toast.error(`${STATUS[data.status]?.[0] || data.status}: ${data.error || ""}`);
    } catch (e) { toast.error(errMsg(e)); }
    onDone();
  };
  if (job.status === "processed") return null;
  const canAssign = ["unassigned", "rejected", "failed", "no_attachments"].includes(job.status);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {job.status !== "unassigned" && (
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid={`scan-job-retry-${job.id}`}
          onClick={() => run(() => api.post(`/scan-inbound/${job.id}/retry`), "Scan importiert")}>
          <ArrowClockwise size={12} /> Erneut verarbeiten
        </Button>
      )}
      {canAssign && platform && (
        <select className="h-7 rounded border border-slate-300 text-xs px-1" value={tid} onChange={(e) => setTid(e.target.value)}
          data-testid={`scan-job-tenant-select-${job.id}`}>
          <option value="">Mandant wählen…</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.display_name || t.name}</option>)}
        </select>
      )}
      {canAssign && (
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={platform && !tid} data-testid={`scan-job-assign-${job.id}`}
          onClick={() => run(() => api.post(`/scan-inbound/${job.id}/assign`, { tenant_id: tid || null }), "Scan zugeordnet und importiert")}>
          {platform ? "Zuordnen" : "Diesem Mandat zuordnen"}
        </Button>
      )}
    </div>
  );
}

function JobRow({ job, platform, tenants, onDone }) {
  const [label, cls] = STATUS[job.status] || [job.status, "bg-slate-100"];
  return (
    <div className="py-2 border-b border-slate-100 last:border-0 space-y-1" data-testid={`scan-job-${job.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge className={`${cls} hover:${cls} font-medium`} data-testid={`scan-job-status-${job.id}`}>{label}</Badge>
        <span className="font-medium text-slate-900 truncate max-w-[260px]">{job.subject || "(ohne Betreff)"}</span>
        <span className="text-slate-500 truncate max-w-[220px]">{job.from}</span>
        {platform && job.tenant_name && <span className="text-slate-500">· {job.tenant_name}</span>}
        <span className="text-slate-400 ml-auto">{fmtDateTime(job.email_created_at || job.created_at)} · {job.attempts || 0}×</span>
      </div>
      {job.error && <div className="text-[11px] text-rose-700 break-all" data-testid={`scan-job-error-${job.id}`}>{job.error}</div>}
      {job.skipped?.length > 0 && <div className="text-[11px] text-slate-500">Übersprungen: {job.skipped.join(", ")}</div>}
      <JobActions job={job} platform={platform} tenants={tenants} onDone={onDone} />
    </div>
  );
}

export default function ScanInboundLog({ platform = false }) {
  const [data, setData] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/scan-inbound").then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); if (platform) api.get("/tenants").then(({ data }) => setTenants(data)); }, [platform]);
  const backfill = async () => {
    setBusy(true);
    try {
      const { data: s } = await api.post("/scan-inbound/backfill");
      toast.success(`Nachimport: ${s.checked} geprüft · ${s.processed} importiert · ${s.failed} fehlgeschlagen · ${s.unassigned} nicht zugeordnet`);
      if (s.resend_list_error) toast.error(`Resend-Liste nicht abrufbar: ${s.resend_list_error}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    load();
  };
  const items = data?.items || [];
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2 bg-white" data-testid="scan-inbound-log">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Tray size={16} /> Eingangsverarbeitung (Scan-E-Mails)</div>
        {Object.entries(data?.counts || {}).map(([k, v]) => (
          <span key={k} className="text-[11px] text-slate-500" data-testid={`scan-inbound-count-${k}`}>{STATUS[k]?.[0] || k}: {v}</span>
        ))}
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" variant="ghost" className="h-7" onClick={load} data-testid="scan-inbound-reload"><ArrowClockwise size={13} /></Button>
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={busy} onClick={backfill} data-testid="scan-inbound-backfill">
            <DownloadSimple size={13} /> {busy ? "Importiere…" : "Verpasste Scans nachimportieren"}
          </Button>
        </div>
      </div>
      {data && items.length === 0 && <div className="text-xs text-slate-500" data-testid="scan-inbound-empty">Noch keine eingegangenen Scan-E-Mails protokolliert.</div>}
      <div className="max-h-80 overflow-auto">
        {items.map((j) => <JobRow key={j.id} job={j} platform={platform} tenants={tenants} onDone={load} />)}
      </div>
    </div>
  );
}
